import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpEvent, HttpEventType, HttpContext } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { FileManagerDriver } from './file-manager-driver.interface';
import type {
    FsData,
    DeleteRequest,
    DeleteResult,
    CreateItemRequest,
    RenameRequest,
    TransferRequest,
    FileOperationResult,
    SearchResult,
    QuotaResponse,
} from '../models/api';
import { ATTACHMENTS_HANDLER_CONFIG } from '../config/attachments-handler.config';
import { SKIP_ERROR_TOAST } from '../http/http-context-tokens';

@Injectable({
    providedIn: 'root',
})
export class VueFinderDriver implements FileManagerDriver {
    private readonly http = inject(HttpClient);
    private readonly config = inject(ATTACHMENTS_HANDLER_CONFIG);
    private readonly baseUrl = this.config.apiBaseUrl;
    private readonly fsManagerBaseUrl = this.config.apiBaseUrl.substring(0, this.config.apiBaseUrl.lastIndexOf('/'));

    listFiles(path: string, options?: { skipErrorToast?: boolean }): Observable<FsData> {
        const params = new HttpParams().set('path', path);
        const context = new HttpContext().set(SKIP_ERROR_TOAST, options?.skipErrorToast ?? false);
        return this.http.get<FsData>(`${this.baseUrl}/`, { params, context });
    }

    uploadFiles(path: string, files: File[]): Observable<{ progress: number; data?: any }> {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append('file', file, file.name);
        });

        const params = new HttpParams().set('path', path);

        return this.http.post(`${this.baseUrl}/upload`, formData, {
            params,
            reportProgress: true,
            observe: 'events'
        }).pipe(
            map((event: HttpEvent<any>) => {
                switch (event.type) {
                    case HttpEventType.UploadProgress:
                        const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
                        return { progress };
                    case HttpEventType.Response:
                        return { progress: 100, data: event.body };
                    default:
                        return { progress: 0 };
                }
            })
        );
    }

    createFolder(path: string, name: string): Observable<FileOperationResult> {
        const request: CreateItemRequest = { path, name };
        return this.http.post<FileOperationResult>(`${this.baseUrl}/create-folder`, request);
    }

    createFile(path: string, name: string): Observable<FileOperationResult> {
        const request: CreateItemRequest = { path, name };
        return this.http.post<FileOperationResult>(`${this.baseUrl}/create-file`, request);
    }

    deleteFiles(path: string, items: Array<{ path: string; type: 'file' | 'dir' }>): Observable<DeleteResult> {
        const request: DeleteRequest = { path, items };
        return this.http.post<DeleteResult>(`${this.baseUrl}/delete`, request);
    }

    searchFiles(path: string, filter?: string, deep?: boolean, size?: 'all' | 'small' | 'medium' | 'large'): Observable<SearchResult> {
        let params = new HttpParams().set('path', path);

        if (filter) {
            params = params.set('filter', filter);
        }
        if (deep !== undefined) {
            params = params.set('deep', deep.toString());
        }
        if (size) {
            params = params.set('size', size);
        }

        return this.http.get<SearchResult>(`${this.baseUrl}/search`, { params });
    }

    renameFile(path: string, item: string, name: string): Observable<FileOperationResult> {
        const request: RenameRequest = { path, item, name };
        return this.http.post<FileOperationResult>(`${this.baseUrl}/rename`, request);
    }

    moveFiles(fromSource: string, sources: string[], toSource: string): Observable<FileOperationResult> {
        const request: TransferRequest = { sources, destination: toSource, path: fromSource };
        return this.http.post<FileOperationResult>(`${this.baseUrl}/move`, request);
    }

    getPreviewUrl(path: string): string {
        return `${this.baseUrl}/preview?path=${encodeURIComponent(path)}`;
    }

    getDownloadUrl(path: string): string {
        return `${this.baseUrl}/download?path=${encodeURIComponent(path)}`;
    }

    getFileSystemQuota(storage: string): Observable<QuotaResponse> {
        return this.http.get<QuotaResponse>(`${this.fsManagerBaseUrl}/filesystem-quota/${storage}`);
    }
}
